import React, { memo, useEffect, useMemo, useState } from 'react';

import { Stack } from '@/components/layout';
import { Select } from '@/components/ui/select';
import type { SelectOption } from '@/components/ui/select/types';
import { Text } from '@/components/ui/text';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const DEFAULT_MIN = new Date(1900, 0, 1);
const DEFAULT_MAX = new Date(2199, 11, 31);

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function clampCalendar(d: Date, min: Date, max: Date) {
  const t = d.getTime();
  if (t < min.getTime()) return new Date(min);
  if (t > max.getTime()) return new Date(max);
  return new Date(d);
}

function initialYMD(date: Date | null | undefined, min: Date, max: Date) {
  const base = date && !Number.isNaN(date.getTime()) ? startOfDay(date) : min;
  const c = clampCalendar(base, min, max);
  return { y: c.getFullYear(), m: c.getMonth(), d: c.getDate() };
}

function buildDate(y: number, m: number, day: number, min: Date, max: Date) {
  const dim = new Date(y, m + 1, 0).getDate();
  let dd = Math.min(Math.max(day, 1), dim);
  if (y === min.getFullYear() && m === min.getMonth()) {
    dd = Math.max(dd, min.getDate());
  }
  if (y === max.getFullYear() && m === max.getMonth()) {
    dd = Math.min(dd, max.getDate());
  }
  let candidate = new Date(y, m, dd);
  if (candidate.getTime() < min.getTime()) candidate = new Date(min);
  if (candidate.getTime() > max.getTime()) candidate = new Date(max);
  return candidate;
}

export type DatePickerSelectFieldsProps = {
  date?: Date | null;
  minimumDate?: Date;
  maximumDate?: Date;
  onDateChange: (date: Date) => void;
};

export const DatePickerSelectFields = memo(function DatePickerSelectFields({
  date,
  minimumDate = DEFAULT_MIN,
  maximumDate = DEFAULT_MAX,
  onDateChange,
}: DatePickerSelectFieldsProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  const min = startOfDay(minimumDate);
  const max = startOfDay(maximumDate);

  const init = initialYMD(date, min, max);
  const [y, setY] = useState(init.y);
  const [m, setM] = useState(init.m);
  const [d, setD] = useState(init.d);

  const yearOptions: SelectOption[] = useMemo(() => {
    const lo = min.getFullYear();
    const hi = max.getFullYear();
    const out: SelectOption[] = [];
    for (let yr = lo; yr <= hi; yr++) {
      out.push({ value: String(yr), label: String(yr) });
    }
    return out;
  }, [min, max]);

  const monthOptions: SelectOption[] = useMemo(() => {
    const lo = y === min.getFullYear() ? min.getMonth() : 0;
    const hi = y === max.getFullYear() ? max.getMonth() : 11;
    const out: SelectOption[] = [];
    for (let mi = lo; mi <= hi; mi++) {
      out.push({
        value: String(mi),
        label: MONTH_LABELS[mi] ?? String(mi + 1),
      });
    }
    return out;
  }, [y, min, max]);

  const dayOptions: SelectOption[] = useMemo(() => {
    const dim = new Date(y, m + 1, 0).getDate();
    let lo = 1;
    let hi = dim;
    if (y === min.getFullYear() && m === min.getMonth()) lo = min.getDate();
    if (y === max.getFullYear() && m === max.getMonth()) hi = max.getDate();
    const out: SelectOption[] = [];
    for (let di = lo; di <= hi; di++) {
      out.push({
        value: String(di),
        label: String(di).padStart(2, '0'),
      });
    }
    return out;
  }, [y, m, min, max]);

  useEffect(() => {
    const allowed = monthOptions.map(o => Number(o.value));
    if (!allowed.includes(m)) {
      setM(allowed[0] ?? 0);
    }
  }, [monthOptions, m]);

  useEffect(() => {
    const allowed = dayOptions.map(o => Number(o.value));
    if (!allowed.includes(d)) {
      setD(allowed[0] ?? 1);
    }
  }, [dayOptions, d]);

  useEffect(() => {
    onDateChange(buildDate(y, m, d, min, max));
  }, [y, m, d, min, max, onDateChange]);

  return (
    <Stack direction="row" gap={scale(10)} style={styles.row}>
      <Stack flex={1} gap={scale(6)}>
        <Text variant="Font Inter/Subtitle/Regular 11px" style={styles.fieldLabel}>
          MONTH
        </Text>
        <Select
          options={monthOptions}
          value={String(m)}
          onValueChange={v => setM(Number(v))}
          placeholder="Month"
          closeOnSelect>
          <Select.Trigger style={styles.selectTrigger} hideIcon={false} />
          <Select.Content maxHeight={scale(280)} />
        </Select>
      </Stack>

      <Stack flex={1} gap={scale(6)}>
        <Text variant="Font Inter/Subtitle/Regular 11px" style={styles.fieldLabel}>
          DAY
        </Text>
        <Select
          options={dayOptions}
          value={String(d)}
          onValueChange={v => setD(Number(v))}
          placeholder="Day"
          closeOnSelect>
          <Select.Trigger style={styles.selectTrigger} hideIcon={false} />
          <Select.Content maxHeight={scale(280)} />
        </Select>
      </Stack>

      <Stack flex={1} gap={scale(6)}>
        <Text variant="Font Inter/Subtitle/Regular 11px" style={styles.fieldLabel}>
          YEAR
        </Text>
        <Select
          options={yearOptions}
          value={String(y)}
          onValueChange={v => setY(Number(v))}
          placeholder="Year"
          closeOnSelect>
          <Select.Trigger style={styles.selectTrigger} hideIcon={false} />
          <Select.Content maxHeight={scale(280)} />
        </Select>
      </Stack>
    </Stack>
  );
});

DatePickerSelectFields.displayName = 'DatePickerSelectFields';

const useStyles = createStyles(({ scale }) => ({
  row: {
    width: '100%',
    marginBottom: scale(8),
  },
  fieldLabel: {
    color: colors['Labels/Secondary'],
    letterSpacing: 0.4,
  },
  selectTrigger: {
    minHeight: scale({ phone: 44, tablet: 48 }),
  },
}));
